import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useViewer } from '@treams/treams-hooks/useViewer';
import { MoreAlternateIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { LocalizedAttributedString, LocalizedString } from '@treams/treams-network';
import { IconButton } from '@treams/treams-ui/common/button/IconButton';
import { Sidebar } from '@treams/treams-ui/common/Sidebar';
import { Spinner } from '@treams/treams-ui/common/Spinner';

import { useJobTitleName } from '../../job/useJobTitleName';
import PopoverConfirm from '../../packages/kakapo-ui-kit/components/popovers/PopoverConfirm';

const Element = styled.div`
	margin-left: 8px;
	position: relative;
`;

type Props = {
	jobPosition: {
		id: string;
		jobTitle: {
			id: string;
			title: LocalizedString;
			description?: LocalizedAttributedString | null | undefined;
		};
		startDate?: string;
		endDate?: string;
		externalReferenceID: string | null | undefined;
		jobLevelID?: string;
	};
};

export const GrowthPathSidebarMyJobPositionItem: FC<Props> = ({ jobPosition }) => {
	const { t } = useTranslation();
	const jobTitleName = useJobTitleName();
	const history = useHistory();
	const viewer = useViewer();
	const { params } = useRouteMatch<{ userID: string; careerID: string; jobTitleID: string }>();

	const queryParams = new URLSearchParams(location.search);
	const stringifiedQueryParams = queryParams.toString();
	const queryParamsString = stringifiedQueryParams ? `?${stringifiedQueryParams}` : '';
	const [showConfirm, setShowConfirm] = useState<boolean>(false);

	const { refetch: userRefetch } = graphql.useUserQuery({
		variables: { id: params.userID ?? viewer!.id },
	});

	const [update, { loading: loadingUpdateJobPosition }] = graphql.useUpdateJobPositionMutation();

	const terminateJobPosition = async () => {
		if (!showConfirm) {
			return;
		}

		await update({
			variables: {
				input: {
					id: jobPosition.id,
					jobTitleID: jobPosition.jobTitle.id,
					startDate: jobPosition.startDate,
					userID: params.userID,
					endDate: new Date(),
					externalReferenceID: jobPosition.externalReferenceID,
					jobLevelID: jobPosition.jobLevelID,
				},
			},
		});
		await userRefetch();
		history.push(`/users/${params.userID}/growth-path/my-job-title/all`);
	};

	const onConfirm = (confirmed: boolean) => {
		if (confirmed) {
			terminateJobPosition();
		} else {
			setShowConfirm(false);
		}
	};

	return (
		<Flex justifyContent="space-between" alignItems="center" mt={2}>
			<Box flex={4}>
				<Sidebar.Item
					small
					title={jobTitleName(jobPosition.jobTitle)}
					path={
						params.userID
							? `/users/${params.userID}/growth-path/my-job-title/${jobPosition.jobTitle.id}${queryParamsString}`
							: `/growth-path/my-job-title/${jobPosition.jobTitle.id}${queryParamsString}`
					}
				/>
			</Box>

			<Element>
				{showConfirm && (
					<PopoverConfirm
						title={t('growth.path.my.job.end.confirm.title')}
						description={t('growth.path.my.job.end.confirm.description')}
						confirmButtonTitle={t('growth.path.my.job.end.confirm.confirmButton.title')}
						cancelButtonTitle={t('growth.path.my.job.end.confirm.cancelButton.title')}
						onConfirm={onConfirm}
						onMouseLeave={() => setShowConfirm(false)}
					/>
				)}

				{loadingUpdateJobPosition && <Spinner />}
				{!loadingUpdateJobPosition && !jobPosition.externalReferenceID && (
					<IconButton
						type="plain"
						icon={<MoreAlternateIcon />}
						onClick={(event) => {
							event.stopPropagation();
							setShowConfirm(!showConfirm);
						}}
					/>
				)}
			</Element>
		</Flex>
	);
};
